import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Button,
  Collapse
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  MobileFriendly as MobileIcon,
  Search as SeoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { websiteAnalyzer } from '../utils/websiteAnalyzer';

const WebsiteAnalyzer = ({ website, businessName }) => {
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    security: true,
    performance: true,
    seo: true,
    mobile: true
  });

  useEffect(() => {
    if (website) {
      analyzeWebsite(website);
    }
  }, [website]);

  const analyzeWebsite = async (url) => {
    setIsAnalyzing(true);
    setError('');
    
    try {
      // Utilisation du service d'analyse
      const analysis = await websiteAnalyzer.analyzeWebsite(url);
      setAnalysis(analysis);
    } catch (err) {
      setError('Erreur lors de l\'analyse: ' + err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };



  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Bon';
    if (score >= 40) return 'Moyen';
    return 'Faible';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Priorité Haute';
      case 'medium': return 'Priorité Moyenne';
      case 'low': return 'Priorité Basse';
      default: return 'Inconnu';
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderIssueList = (issues) => {
    if (!issues || issues.length === 0) {
      return (
        <ListItem>
          <ListItemIcon>
            <CheckIcon color="success" />
          </ListItemIcon>
          <ListItemText primary="Aucun problème détecté" />
        </ListItem>
      );
    }

    return issues.map((issue, index) => (
      <ListItem key={index}>
        <ListItemIcon>
          <ErrorIcon color="error" />
        </ListItemIcon>
        <ListItemText primary={issue} />
      </ListItem>
    ));
  };

  if (isAnalyzing) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          🔍 Analyse technique en cours...
        </Typography>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Analyse du site {website}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analysis) {
    return null;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Score global */}
      <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                📊 Score Global
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h3" color={`${getScoreColor(analysis.overallScore)}.main`}>
                  {analysis.overallScore}/100
                </Typography>
                <Chip
                  label={getScoreLabel(analysis.overallScore)}
                  color={getScoreColor(analysis.overallScore)}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                🎯 Priorité de Prospection
              </Typography>
              <Chip
                label={getPriorityLabel(analysis.priority)}
                color={getPriorityColor(analysis.priority)}
                size="large"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sections d'analyse */}
      <Grid container spacing={2}>
        {/* Sécurité */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ mr: 1 }} />
                  Sécurité ({analysis.security.securityScore || 0}/100)
                </Typography>
                <Button
                  size="small"
                  onClick={() => toggleSection('security')}
                  endIcon={expandedSections.security ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expandedSections.security ? 'Réduire' : 'Développer'}
                </Button>
              </Box>
              
              <Collapse in={expandedSections.security}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.security.hasHttps ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="HTTPS" secondary={analysis.security.hasHttps ? "Site sécurisé" : "Site non sécurisé"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.security.hasSsl ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="Certificat SSL" secondary={analysis.security.hasSsl ? "Présent" : "Absent"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.security.hasSecurityHeaders ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="En-têtes de sécurité" secondary={analysis.security.hasSecurityHeaders ? "Configurés" : "Manquants"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.security.hasCsp ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Content Security Policy" secondary={analysis.security.hasCsp ? "Configuré" : "Manquant"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.security.hasSecureCookies ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Cookies sécurisés" secondary={analysis.security.hasSecureCookies ? "Configurés" : "Non sécurisés"} />
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                  {renderIssueList(analysis.security.issues)}
                </List>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <SpeedIcon sx={{ mr: 1 }} />
                  Performance
                </Typography>
                <Button
                  size="small"
                  onClick={() => toggleSection('performance')}
                  endIcon={expandedSections.performance ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expandedSections.performance ? 'Réduire' : 'Développer'}
                </Button>
              </Box>
              
              <Collapse in={expandedSections.performance}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.performance.loadTime < 3 ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Temps de chargement" secondary={`${analysis.performance.loadTime}s`} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.performance.pageSize < 1000 ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Taille de page" secondary={`${Math.round(analysis.performance.pageSize / 1024)}KB`} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.performance.hasOptimizedImages ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Images optimisées" secondary={analysis.performance.hasOptimizedImages ? "Oui" : "Non"} />
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                  {renderIssueList(analysis.performance.issues)}
                </List>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        {/* SEO */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <SeoIcon sx={{ mr: 1 }} />
                  SEO
                </Typography>
                <Button
                  size="small"
                  onClick={() => toggleSection('seo')}
                  endIcon={expandedSections.seo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expandedSections.seo ? 'Réduire' : 'Développer'}
                </Button>
              </Box>
              
              <Collapse in={expandedSections.seo}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.seo.hasMetaDescription ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="Meta description" secondary={analysis.seo.hasMetaDescription ? "Présente" : "Manquante"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.seo.hasTitle ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="Titre de page" secondary={analysis.seo.hasTitle ? "Présent" : "Manquant"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.seo.hasAltImages ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Alt des images" secondary={analysis.seo.hasAltImages ? "Configurés" : "Manquants"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.seo.hasSitemap ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Sitemap" secondary={analysis.seo.hasSitemap ? "Présent" : "Absent"} />
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                  {renderIssueList(analysis.seo.issues)}
                </List>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        {/* Mobile */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <MobileIcon sx={{ mr: 1 }} />
                  Mobile
                </Typography>
                <Button
                  size="small"
                  onClick={() => toggleSection('mobile')}
                  endIcon={expandedSections.mobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expandedSections.mobile ? 'Réduire' : 'Développer'}
                </Button>
              </Box>
              
              <Collapse in={expandedSections.mobile}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.mobile.isResponsive ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="Design responsive" secondary={analysis.mobile.isResponsive ? "Oui" : "Non"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.mobile.hasViewportMeta ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText primary="Meta viewport" secondary={analysis.mobile.hasViewportMeta ? "Présent" : "Manquant"} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {analysis.mobile.hasTouchFriendlyButtons ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Boutons tactiles" secondary={analysis.mobile.hasTouchFriendlyButtons ? "Optimisés" : "Non optimisés"} />
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                  {renderIssueList(analysis.mobile.issues)}
                </List>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recommandations */}
      <Card sx={{ mt: 3, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💡 Recommandations de Prospection
          </Typography>
          <Typography variant="body2" paragraph>
            Ce site présente plusieurs opportunités d'amélioration techniques. 
            {analysis.priority === 'high' && ' Priorité élevée pour la prospection.'}
            {analysis.priority === 'medium' && ' Potentiel intéressant pour la prospection.'}
            {analysis.priority === 'low' && ' Potentiel limité pour la prospection.'}
          </Typography>
          <Typography variant="body2">
            <strong>Points d'amélioration identifiés :</strong>
          </Typography>
          <List dense>
            {!analysis.security.hasHttps && (
              <ListItem>
                <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                <ListItemText primary="Migration vers HTTPS pour améliorer la sécurité et le SEO" />
              </ListItem>
            )}
            {analysis.performance.loadTime > 3 && (
              <ListItem>
                <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                <ListItemText primary="Optimisation des performances pour améliorer l'expérience utilisateur" />
              </ListItem>
            )}
            {!analysis.mobile.isResponsive && (
              <ListItem>
                <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                <ListItemText primary="Adaptation mobile pour toucher plus de clients" />
              </ListItem>
            )}
            {!analysis.seo.hasMetaDescription && (
              <ListItem>
                <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                <ListItemText primary="Amélioration du SEO pour une meilleure visibilité" />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WebsiteAnalyzer;